/**
 * PATH: apps/qa/src/components/inspection/FloorBuilder/VisualPreview.tsx
 * PURPOSE: Visual building diagram preview with floor selection
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { FloorConfig } from './types'
import { FLOOR_TYPE_STYLES } from './constants'
import { Layers, ChevronRight, ChevronDown, ChevronUp, Clock, Sparkles, X } from 'lucide-react'

interface VisualPreviewProps {
  generatedFloors: FloorConfig[]
  stats: { totalFloors: number; totalAreas: number; timeEstimate: { formatted: string } }
}

export function VisualPreview({ generatedFloors, stats }: VisualPreviewProps) {
  const [selectedFloorIndex, setSelectedFloorIndex] = useState<number | null>(null)
  const [hoveredFloorIndex, setHoveredFloorIndex] = useState<number | null>(null)
  const [previewCollapsed, setPreviewCollapsed] = useState(false)

  return (
    <div className="p-4 bg-muted/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Visual Preview</span>
        </div>
        <button
          onClick={() => setPreviewCollapsed(!previewCollapsed)}
          className="lg:hidden p-1 rounded hover:bg-muted text-muted-foreground"
        >
          {previewCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      <div className={cn(
        'transition-all duration-300 overflow-hidden',
        previewCollapsed ? 'max-h-0 lg:max-h-none' : 'max-h-[400px]'
      )}>
        <div className="flex flex-col-reverse gap-1 max-h-[300px] overflow-y-auto py-2 scrollbar-thin">
          {generatedFloors.slice().reverse().map((floor, reverseIndex) => {
            const index = generatedFloors.length - 1 - reverseIndex
            const style = FLOOR_TYPE_STYLES[floor.type]
            const IconComponent = style.icon
            const isSelected = selectedFloorIndex === index
            const isHovered = hoveredFloorIndex === index

            return (
              <button
                key={floor.id}
                onClick={() => setSelectedFloorIndex(isSelected ? null : index)}
                onMouseEnter={() => setHoveredFloorIndex(index)}
                onMouseLeave={() => setHoveredFloorIndex(null)}
                className={cn(
                  'group relative flex items-center justify-between px-3 py-2 rounded-lg border transition-all duration-200',
                  style.bg, style.border,
                  isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                  isHovered && !isSelected && 'scale-[1.02] shadow-md',
                  'hover:shadow-sm'
                )}
              >
                <div className="flex items-center gap-2">
                  <IconComponent className={cn('w-3.5 h-3.5', style.text)} />
                  <span className={cn('text-sm font-medium', style.text)}>{floor.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {floor.areas.length} area{floor.areas.length !== 1 ? 's' : ''}
                  </span>
                  <ChevronRight className={cn('w-4 h-4 text-muted-foreground transition-transform', isSelected && 'rotate-90')} />
                </div>
              </button>
            )
          })}
        </div>

        {/* Selected Floor Editor */}
        {selectedFloorIndex !== null && generatedFloors[selectedFloorIndex] && (
          <div className="mt-3 p-3 rounded-lg border border-primary/30 bg-background animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground">{generatedFloors[selectedFloorIndex].name} Areas</p>
              <button onClick={() => setSelectedFloorIndex(null)} className="p-1 rounded hover:bg-muted text-muted-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {generatedFloors[selectedFloorIndex].areas.map(area => (
                <span
                  key={area.id}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs',
                    area.aiSuggested ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-muted text-muted-foreground'
                  )}
                >
                  {area.aiSuggested && <Sparkles className="w-2.5 h-2.5" />}
                  {area.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-semibold text-foreground">{stats.totalFloors}</p>
              <p className="text-xs text-muted-foreground">Floors</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground">{stats.totalAreas}</p>
              <p className="text-xs text-muted-foreground">Areas</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground flex items-center justify-center gap-1">
                <Clock className="w-3.5 h-3.5" />{stats.timeEstimate.formatted}
              </p>
              <p className="text-xs text-muted-foreground">Est. time</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
